import { getEnvVariable } from '../../../envs';
import { collectStoredTokens, resolveLogoutUrl, resolvePortal, resolveRevokeUrl, revokeSessions } from './sessionRevoke';
// Both redirect helpers below end the current session in some way - share one guard so a burst of
// near-simultaneous triggers (several requests 401ing at once, an idle timeout landing right as a
// request fails) only navigates once. This is a short debounce, NOT a permanent one-shot flag: an
// earlier one-off flag stuck forever once set, which meant a real, unrelated redirect later in the
// same session (e.g. a genuine idle-timeout logout happening well after an earlier 401) would
// silently no-op because the flag from that earlier call was never cleared.
let redirectingUntil = 0;
const REDIRECT_DEBOUNCE_MS = 3000;
const isRedirecting = () => Date.now() < redirectingUntil;
const markRedirecting = () => { redirectingUntil = Date.now() + REDIRECT_DEBOUNCE_MS; };
/**
 * This app runs embedded inside an iframe within a parent shell. Plain `window.location` only
 * addresses this iframe's own document - reloading or reassigning it leaves the outer shell page
 * untouched, which is not what "go to the main page" should mean here. Navigating `window.top`
 * instead moves the actual top-level browser tab, taking the whole shell (iframe included) to the
 * given URL. Assigning `.href` on a cross-origin `window.top` is one of the few cross-origin
 * operations browsers permit (it's how logout/SSO flows are meant to work); reading it is not, so
 * this never inspects `window.top.location`, only ever writes to it.
 *
 * A sandboxed parent iframe (e.g. `sandbox="allow-scripts allow-same-origin"` without
 * `allow-top-navigation`) can silently BLOCK this assignment - no exception is thrown, the
 * navigation just doesn't happen, so a try/catch alone can't detect it. If the assignment actually
 * succeeds, this document unloads shortly after and the fallback below never gets a chance to run;
 * if it was silently blocked, the fallback fires and navigates this frame instead, so the user
 * isn't left stuck with no navigation at all.
 *
 * Exported (beyond this file's own logout/session-reset use) for any other in-app control whose
 * link is a full "host" URL meant to replace the whole shell rather than load inside this iframe -
 * e.g. a `target="_self"` action button - so it doesn't need to reinvent this same fallback.
 */
export const navigateTopLevel = (url) => {
    try {
        (window.top || window).location.href = url;
    }
    catch {
        // ignore - fall through to the fallback below
    }
    setTimeout(() => {
        window.location.href = url;
    }, 300);
};
// localStorage key prefixes Utils/auth.ts (getApplicationToken) recognises as this app's tokens -
// the full key carries a dynamic module/title suffix (e.g. `internaltoken_APPS_ONESTOP`).
const TOKEN_PREFIXES = ['internaltoken', 'externaltoken'];
const findTokenKeys = () => {
    const keys = [];
    try {
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && TOKEN_PREFIXES.some((prefix) => key.startsWith(prefix)))
                keys.push(key);
        }
    }
    catch {
        // localStorage unavailable - treated as "no tokens"
    }
    return keys;
};
/** True while this app holds a signed-in token - lets the idle timer stay dormant for anonymous visitors. */
export const hasSessionToken = () => findTokenKeys().length > 0;
const clearSessionTokens = () => {
    findTokenKeys().forEach((key) => {
        try {
            localStorage.removeItem(key);
        }
        catch { /* ignore - must not block the redirect */ }
    });
};
/** The sign-in screen: first AuthorizationLogins path, else AuthorizationScreen, else the app root. */
const resolveSignInUrl = () => {
    const logins = getEnvVariable('AuthorizationLogins');
    const firstLoginPath = Array.isArray(logins) ? logins[0]?.AuthorizationPath : undefined;
    return firstLoginPath
        || String(getEnvVariable('AuthorizationScreen') || '').trim()
        || getEnvVariable('Basepath')
        || '/';
};
/**
 * Ends this browser's sessions on the Auth server (SafeAeon v3 EXT-001): every stored token is POSTed to env.json's
 * `Auth` + /api/UserAuth/Logout, else the URL next to `ConnectToken`. Call it BEFORE any token is cleared - afterwards
 * there is nothing left to revoke, and the JWT keeps working on every API until it expires. An app with neither key
 * relies on its logout page (Auth-UI revokes what is still in storage). Waits at most `timeoutMs` and never rejects;
 * true only when the Auth API confirmed every revocation.
 */
export const revokeServerSession = (timeoutMs = 2000) => revokeSessions(resolveRevokeUrl({ Auth: getEnvVariable('Auth'), ConnectToken: getEnvVariable('ConnectToken') }), collectStoredTokens([() => localStorage, () => sessionStorage]), undefined, timeoutMs);
/**
 * The logout page for the signed-in portal: `InternalLogOutUrl` / `StaffLogOutUrl` for I, `ExternalLogout` for E,
 * else `LogOutUrl`. The portal is the `AccessPortal` stored at login, else env `AccessPortal`, else the token prefix.
 * Empty string when none is configured.
 */
export const resolveSessionLogoutUrl = () => resolveLogoutUrl(getEnvVariable, resolvePortal([() => localStorage, () => sessionStorage], getEnvVariable('AccessPortal')));
const goToLogout = async () => {
    // Read before anything else: the portal comes from storage. Revoke here rather than leave it to Auth-UI: its
    // logout page only revokes tokens still in storage, and the branch below (no logout page) clears them.
    const logoutUrl = resolveSessionLogoutUrl();
    await revokeServerSession();
    if (logoutUrl) {
        // Auth-UI clears the tokens itself (and revokes again, harmlessly, if any are left).
        navigateTopLevel(logoutUrl);
        return;
    }
    // No logout page configured - nothing else will drop the tokens, so do it here before sending
    // the user to sign in, otherwise the app would just pick the same session straight back up.
    clearSessionTokens();
    navigateTopLevel(resolveSignInUrl());
};
/**
 * Used by SessionTimeoutManager for an explicit logout (the countdown expiring, or the user
 * clicking "Log out now"). Ends the session on the Auth server first (revokeServerSession), then
 * sends the browser to the portal's logout page (resolveSessionLogoutUrl) - its own callbackurl brings
 * the user back to the app's base path afterward. Without one the tokens are cleared here and
 * the user is sent to the sign-in screen (AuthorizationLogins / AuthorizationScreen).
 */
export const redirectToLogout = () => {
    if (isRedirecting())
        return;
    markRedirecting();
    void goToLogout();
};
// If a 401-triggered reload lands on another 401 within this window, the session is genuinely
// dead rather than this being a one-off blip - reloading again would just repeat the same loop.
const REDIRECT_LOOP_STORAGE_KEY = '__dashboard_401_redirect__';
const REDIRECT_LOOP_WINDOW_MS = 30000;
const REDIRECT_LOOP_MAX_ATTEMPTS = 1;
const readRedirectAttempts = () => {
    try {
        const raw = sessionStorage.getItem(REDIRECT_LOOP_STORAGE_KEY);
        if (!raw)
            return { count: 0, at: 0 };
        const parsed = JSON.parse(raw);
        return { count: Number(parsed?.count) || 0, at: Number(parsed?.at) || 0 };
    }
    catch {
        return { count: 0, at: 0 };
    }
};
const writeRedirectAttempts = (count) => {
    try {
        sessionStorage.setItem(REDIRECT_LOOP_STORAGE_KEY, JSON.stringify({ count, at: Date.now() }));
    }
    catch {
        // sessionStorage unavailable (private browsing, etc.) - loop detection just won't apply,
        // every 401 gets the normal reload treatment.
    }
};
/**
 * Used by the 401 handler (library/api/rest/index.tsx). A 401 mid-session is normally treated as
 * "reload from a clean slate" rather than a full external logout: navigate the TOP-LEVEL window
 * (not just this iframe) to env.json's Basepath, so the whole shell - not just the embedded
 * dashboard - re-initializes from scratch instead of silently reloading behind the parent page.
 *
 * BUT if that reload immediately hits another 401 (tracked via sessionStorage, which survives the
 * reload), refreshing clearly isn't fixing anything - the session itself is dead server-side, and
 * repeating this would just trap the user on a flashing "Loading dashboard..." screen forever.
 * At that point it falls back to a real logout, the only thing that can produce a valid session.
 */
export const redirectToMainAndRefresh = () => {
    if (isRedirecting())
        return;
    markRedirecting();
    const { count, at } = readRedirectAttempts();
    const isWithinLoopWindow = Date.now() - at < REDIRECT_LOOP_WINDOW_MS;
    const nextCount = isWithinLoopWindow ? count + 1 : 1;
    if (nextCount > REDIRECT_LOOP_MAX_ATTEMPTS) {
        try {
            sessionStorage.removeItem(REDIRECT_LOOP_STORAGE_KEY);
        }
        catch { /* ignore */ }
        void goToLogout();
        return;
    }
    writeRedirectAttempts(nextCount);
    const basePath = getEnvVariable('Basepath');
    navigateTopLevel(basePath || '/');
};
